import { useCallback, useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { getBlockSortableId, type VisibleBlock } from "../../utils";
import { SortableBlockRows } from "./SortableBlockRows";

export interface SortableBlockListProps {
  visibleBlocks: VisibleBlock[];
  enabled: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  renderOverlay: (visible: VisibleBlock) => ReactNode;
  children: ReactNode;
}

export const SortableBlockList = ({
  visibleBlocks,
  enabled,
  onDragEnd,
  renderOverlay,
  children,
}: SortableBlockListProps) => {
  const [activeBlock, setActiveBlock] = useState<VisibleBlock>();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setActiveBlock(visibleBlocks.find(({ block, index }) => getBlockSortableId(block, index) === active.id));
    },
    [visibleBlocks]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveBlock(undefined);
      onDragEnd(event);
    },
    [onDragEnd]
  );

  if (!enabled) return <SortableBlockRows isDragging={false}>{children}</SortableBlockRows>;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveBlock(undefined)}
    >
      <SortableContext
        items={visibleBlocks.map(({ block, index }) => getBlockSortableId(block, index))}
        strategy={verticalListSortingStrategy}
      >
        <SortableBlockRows isDragging={!!activeBlock}>{children}</SortableBlockRows>
      </SortableContext>
      <DragOverlay>
        {activeBlock && (
          <div className="bg-background shadow-lg rounded-sm max-h-[400px] overflow-hidden cursor-grabbing">
            {renderOverlay(activeBlock)}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
